import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clustersAPI, vpnProfilesAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './AddCluster.css';

function ImportCluster() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  useEffect(() => {
    if (!isSuperAdmin) navigate('/clusters');
  }, [isSuperAdmin, navigate]);

  const [formData, setFormData] = useState({
    name: '',
    environment: 'dev',
    connection_type: 'direct',
    kubeconfig: '',
    vpn_profile_id: '',
    // Rancher transport
    rancher_server_url: '',
    rancher_cluster_id: '',
    rancher_token: '',
    rancher_verify_tls: false,
  });
  // Within Rancher mode: register a brand-new cluster with Rancher ourselves
  // (SarvKube calls Rancher's API server-side, no token from the user) vs. the
  // existing manual path (paste a rancher_server_url/cluster_id/token you already
  // have). Default to self-service — that's the whole point of this flow existing.
  const [rancherSelfService, setRancherSelfService] = useState(true);
  const [vpnProfiles, setVpnProfiles] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // After agent-mode import succeeds, store the manifest for display
  const [agentManifest, setAgentManifest] = useState(null);
  const [importedCluster, setImportedCluster] = useState(null);
  // After self-service Rancher registration, the kubectl command to hand back, and
  // the live status while we poll for the admin to have run it.
  const [rancherKubectlCommand, setRancherKubectlCommand] = useState(null);
  const [rancherRegStatus, setRancherRegStatus] = useState('pending');
  // Rancher API keys on this install cap out at 90 days — there's no non-expiring
  // option — so renewal is a manual step (mint a new token, update secrets.env,
  // redeploy) with nothing else to remind the admin. Warn here once it's close.
  const [adminTokenWarning, setAdminTokenWarning] = useState(null);

  useEffect(() => {
    vpnProfilesAPI.list().then(r => setVpnProfiles(r.data)).catch(() => {});
    clustersAPI.getRancherAdminTokenStatus().then(r => {
      const { configured, days_remaining: days } = r.data;
      if (configured && typeof days === 'number' && days <= 14) {
        setAdminTokenWarning(
          days <= 0
            ? 'The self-service registration credential has expired — mint a new Rancher API key and update secrets.env.'
            : `The self-service registration credential expires in ${days} day${days === 1 ? '' : 's'} — mint a new Rancher API key and update secrets.env before then.`
        );
      }
    }).catch(() => {});
  }, []);

  // Poll for Rancher-Active status every 10s, same pattern as ClusterAgent.js's
  // agent-connect poll — this is a single pending/active/error check, not a
  // multi-stage install with steps to narrate, so a plain interval (not the
  // WebSocket mechanism used for kubeadm provisioning) is the right fit.
  useEffect(() => {
    if (!importedCluster || !rancherKubectlCommand) return;
    if (rancherRegStatus === 'active' || rancherRegStatus === 'error') return;
    const interval = setInterval(async () => {
      try {
        const r = await clustersAPI.getRancherRegistrationStatus(importedCluster.id);
        if (r.data.status === 'active' || r.data.status === 'error') {
          setRancherRegStatus(r.data.status);
          clearInterval(interval);
        }
      } catch { /* ignore — keep polling */ }
    }, 10000);
    return () => clearInterval(interval);
  }, [importedCluster, rancherKubectlCommand, rancherRegStatus]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Checkboxes carry their state in `checked`, not `value` (which is the string "on").
    setFormData(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'kubeconfig') setTestResult(null);
  };

  const handleKubeconfigFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(f => ({ ...f, kubeconfig: event.target.result }));
        setTestResult(null);
      };
      reader.readAsText(file);
    }
  };

  const isAgentMode = formData.connection_type === 'agent';
  // Rancher mode: the cluster is reached through a Rancher server's k8s proxy, so there's
  // no kubeconfig to upload and no agent to install — just Rancher's URL, cluster id, token.
  const isRancherMode = formData.connection_type === 'rancher';
  // Both agent and rancher modes skip the kubeconfig upload + test-connection step.
  const needsKubeconfig = !isAgentMode && !isRancherMode;

  const handleTestConnection = async () => {
    if (!formData.kubeconfig.trim()) {
      setError('Please provide a kubeconfig first');
      return;
    }
    setError(null);
    setTesting(true);
    setTestResult(null);
    try {
      const testPayload = { kubeconfig: formData.kubeconfig };
      if (formData.vpn_profile_id) {
        testPayload.vpn_profile_id = parseInt(formData.vpn_profile_id, 10);
      }
      const response = await clustersAPI.testConnection(testPayload);
      setTestResult({ success: true, ...response.data });
    } catch (err) {
      setTestResult({
        success: false,
        message: err.response?.data?.detail || 'Connection failed — check your kubeconfig',
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name) { setError('Cluster name is required'); return; }

    if (needsKubeconfig) {
      if (!formData.kubeconfig.trim()) { setError('Kubeconfig is required'); return; }
      if (!testResult?.success) { setError('Please test the connection before importing'); return; }
    }

    if (isRancherMode && !rancherSelfService) {
      if (!formData.rancher_server_url?.trim()) { setError('Management server URL is required'); return; }
      if (!formData.rancher_cluster_id?.trim()) { setError('Cluster ID is required'); return; }
      if (!formData.rancher_token?.trim()) { setError('API token is required'); return; }
    }

    setLoading(true);
    try {
      // Self-service Rancher: a completely different endpoint/payload (just
      // name+environment — SarvKube does the Rancher-side registration itself).
      if (isRancherMode && rancherSelfService) {
        const response = await clustersAPI.registerWithRancher({
          name: formData.name,
          environment: formData.environment,
        });
        setImportedCluster(response.data.cluster);
        setRancherKubectlCommand(response.data.kubectl_command);
        setRancherRegStatus('pending');
        return;
      }

      const payload = {
        name: formData.name,
        environment: formData.environment,
        connection_type: formData.connection_type,
      };
      if (needsKubeconfig) {
        payload.kubeconfig = formData.kubeconfig;
        if (formData.vpn_profile_id) {
          payload.vpn_profile_id = parseInt(formData.vpn_profile_id, 10);
        }
      }
      if (isRancherMode) {
        payload.rancher_server_url = formData.rancher_server_url.trim();
        payload.rancher_cluster_id = formData.rancher_cluster_id.trim();
        payload.rancher_token = formData.rancher_token.trim();
        payload.rancher_verify_tls = !!formData.rancher_verify_tls;
      }

      const response = await clustersAPI.importCluster(payload);
      const cluster = response.data;

      if (isAgentMode) {
        // Fetch the install manifest
        try {
          const manifestResp = await clustersAPI.getAgentManifest(cluster.id);
          setAgentManifest(manifestResp.data.manifest);
          setImportedCluster(cluster);
        } catch {
          navigate(`/clusters/${cluster.id}`);
        }
      } else {
        navigate(`/clusters/${cluster.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to import cluster');
    } finally {
      setLoading(false);
    }
  };

  // ── Post-submit: self-service Rancher registration screen ────────────────────
  if (rancherKubectlCommand && importedCluster) {
    const step = (n, title) => (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <div style={{
          minWidth: 28, height: 28, borderRadius: '50%', background: 'var(--brand)',
          color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 13, flexShrink: 0,
        }}>{n}</div>
        <div style={{ paddingTop: 4, color: 'var(--ink-2)', fontSize: 14 }}>{title}</div>
      </div>
    );

    const statusBanner = {
      pending: { cls: 'info', text: 'Waiting for the command below to be run on your cluster...' },
      active: { cls: 'success', text: 'Connected! This cluster is now Active.' },
      error: { cls: 'danger', text: 'Registration failed — check the cluster’s activity log, or try again.' },
    }[rancherRegStatus] || { cls: 'info', text: 'Waiting...' };

    return (
      <div className="add-cluster-container">
        <div className="page-header">
          <h1>Register managed cluster</h1>
        </div>
        <div className="card">
          <div className="banner success" style={{ marginBottom: 20 }}>
            Cluster <strong>{importedCluster.name}</strong> registered.
            Run the command below on your cluster to connect it — no separate
            console or manual setup needed.
          </div>

          <h3 style={{ marginBottom: 16 }}>How to connect your cluster</h3>
          {step(1, <span>SSH into any node of your cluster with <code>kubectl</code> access (or run this from your own machine with the right kubeconfig)</span>)}
          {step(2, <span>Run the command below — it installs a lightweight connector agent that opens an outbound tunnel back to SarvKube's cluster management platform (no inbound access to your cluster needed)</span>)}
          {step(3, <span>Come back here — this page checks automatically every 10 seconds and will show <strong style={{ color: 'var(--color-success-500)' }}>Active</strong> once the connection is detected</span>)}

          <h3 style={{ marginTop: 24, marginBottom: 8 }}>Registration command</h3>
          <div style={{ position: 'relative' }}>
            <textarea
              readOnly
              value={rancherKubectlCommand}
              rows={3}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 12,
                fontFamily: 'monospace',
                background: 'var(--ink)',
                color: 'var(--border)',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div className={`banner ${statusBanner.cls}`} style={{ marginTop: 16 }}>
            {rancherRegStatus === 'pending' && (
              <span style={{ display: 'inline-block', marginRight: 8 }}>&#8987;</span>
            )}
            {statusBanner.text}
          </div>

          <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              className="button button-primary"
              onClick={() => navigator.clipboard.writeText(rancherKubectlCommand).catch(() => {})}
            >
              Copy command
            </button>
            <button
              className="button button-secondary"
              onClick={() => navigate(`/clusters/${importedCluster.id}`)}
              disabled={rancherRegStatus !== 'active'}
            >
              Go to cluster →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Post-submit: agent manifest screen ───────────────────────────────────────
  if (agentManifest && importedCluster) {
    const step = (n, title) => (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <div style={{
          minWidth: 28, height: 28, borderRadius: '50%', background: 'var(--brand)',
          color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 13, flexShrink: 0,
        }}>{n}</div>
        <div style={{ paddingTop: 4, color: 'var(--ink-2)', fontSize: 14 }}>{title}</div>
      </div>
    );

    return (
      <div className="add-cluster-container">
        <div className="page-header">
          <h1>Deploy sarv-agent in your cluster</h1>
        </div>
        <div className="card">
          <div className="banner success" style={{ marginBottom: 20 }}>
            Cluster <strong>{importedCluster.name}</strong> registered successfully.
            Follow the steps below to connect it.
          </div>

          <h3 style={{ marginBottom: 16 }}>How to connect your cluster</h3>

          {step(1, <span>SSH into any node of your cluster (control-plane or worker)</span>)}
          {step(2, <span>Make sure <code>kubectl</code> is available and pointing at the cluster</span>)}
          {step(3, <span>
            Save the manifest below to a file and apply it:
            <div style={{
              background: 'var(--ink)', color: 'var(--color-success-500)', fontFamily: 'monospace',
              fontSize: 13, padding: '10px 14px', borderRadius: 6, marginTop: 8,
            }}>
              kubectl apply -f sarv-agent.yaml
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 4 }}>
              Or pipe directly: &nbsp;
              <code style={{ fontSize: 12 }}>curl -s &lt;url&gt; | kubectl apply -f -</code>
            </div>
          </span>)}
          {step(4, <span>
            The agent will connect back to SarvKube automatically.
            Go to the cluster page — status will show <strong style={{ color: 'var(--color-success-500)' }}>connected</strong> within a few seconds.
          </span>)}

          <h3 style={{ marginTop: 24, marginBottom: 8 }}>sarv-agent.yaml</h3>
          <div style={{ position: 'relative' }}>
            <textarea
              readOnly
              value={agentManifest}
              rows={28}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 12,
                fontFamily: 'monospace',
                background: 'var(--ink)',
                color: 'var(--border)',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              className="button button-primary"
              onClick={() => navigator.clipboard.writeText(agentManifest).catch(() => {})}
            >
              Copy YAML
            </button>
            <button
              className="button button-secondary"
              onClick={() => {
                const blob = new Blob([agentManifest], { type: 'text/yaml' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `sarv-agent-${importedCluster.name}.yaml`;
                a.click();
              }}
            >
              Download YAML
            </button>
            <button
              className="button button-secondary"
              onClick={() => navigate(`/clusters/${importedCluster.id}`)}
            >
              Go to cluster →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────────────
  return (
    <div className="add-cluster-container">
      <div className="page-header">
        <h1>Import Existing Cluster</h1>
        <button className="button button-secondary" onClick={() => navigate('/clusters/add')}>
          Back
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {adminTokenWarning && <div className="banner warning">{adminTokenWarning}</div>}

      <form onSubmit={handleSubmit}>
        <div className="card">
          <h2>Cluster Details</h2>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Cluster Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="my-prod-cluster"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="environment">Environment *</label>
              <select id="environment" name="environment" value={formData.environment} onChange={handleInputChange} required>
                <option value="dev">Development</option>
                <option value="stage">Staging</option>
                <option value="prod">Production</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="connection_type">Connection Method *</label>
            <select
              id="connection_type"
              name="connection_type"
              value={formData.connection_type}
              onChange={handleInputChange}
            >
              <option value="direct">Direct — cluster API publicly reachable</option>
              <option value="vpn">VPN — connect via WireGuard / Pritunl</option>
              <option value="agent">Agent — cluster is behind firewall (no inbound ports needed)</option>
              <option value="rancher">Managed Cluster — connect via SarvKube's cluster management platform</option>
            </select>
            {isAgentMode && (
              <div className="banner info" style={{ marginTop: 8 }}>
                <strong>Agent mode:</strong> SarvKube will generate an agent token.
                Deploy the provided YAML inside your cluster — the agent connects <em>outbound</em> to SarvKube.
                No firewall rules or kubeconfig needed.
              </div>
            )}
            {isRancherMode && (
              <div className="banner info" style={{ marginTop: 8 }}>
                <strong>Managed Cluster mode:</strong> SarvKube reaches the cluster through its
                management platform's Kubernetes proxy. An outbound tunnel from the cluster is
                already in place, so no kubeconfig and no SarvKube agent are needed.
              </div>
            )}
            {isRancherMode && (
              <div className="form-group" style={{ marginTop: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={rancherSelfService}
                    onChange={(e) => setRancherSelfService(e.target.checked)}
                  />
                  Register a new managed cluster for me
                </label>
                <small>
                  {rancherSelfService
                    ? "SarvKube creates the registration and gives you a single command to run — no separate console or manual setup needed."
                    : 'Uncheck only if you already have a cluster ID and API token from an existing registration.'}
                </small>
              </div>
            )}
          </div>

          {formData.connection_type === 'vpn' && (
            <div className="form-group">
              <label htmlFor="vpn_profile_id">VPN Profile</label>
              <select
                id="vpn_profile_id"
                name="vpn_profile_id"
                value={formData.vpn_profile_id}
                onChange={handleInputChange}
              >
                <option value="">Select a VPN profile</option>
                {vpnProfiles.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.vpn_type === 'openvpn' ? 'OpenVPN/Pritunl' : 'WireGuard'})
                  </option>
                ))}
              </select>
              {vpnProfiles.length === 0 && (
                <small><a href="/vpn-profiles" style={{ color: 'var(--brand)' }}>Create a VPN profile</a> first.</small>
              )}
            </div>
          )}
        </div>

        {/* Rancher connection details — only for rancher mode, and only when NOT
            using self-service registration (that path needs no manual fields) */}
        {isRancherMode && !rancherSelfService && (
          <div className="card">
            <h2>Managed Cluster Connection</h2>
            <p style={{ color: 'var(--muted)', fontSize: '14px', marginBottom: '8px' }}>
              Find the cluster ID in your management console's URL when you open the cluster
              (e.g. <code>/c/c-z6gqs/</code>), and create an API token under
              <em> Profile → API &amp; Keys</em>.
            </p>

            <div className="form-group">
              <label htmlFor="rancher_server_url">Management Server URL *</label>
              <input
                type="text"
                id="rancher_server_url"
                name="rancher_server_url"
                value={formData.rancher_server_url}
                onChange={handleInputChange}
                placeholder="https://cluster-manager.example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="rancher_cluster_id">Cluster ID *</label>
              <input
                type="text"
                id="rancher_cluster_id"
                name="rancher_cluster_id"
                value={formData.rancher_cluster_id}
                onChange={handleInputChange}
                placeholder="c-z6gqs"
              />
            </div>

            <div className="form-group">
              <label htmlFor="rancher_token">API Token *</label>
              <input
                type="password"
                id="rancher_token"
                name="rancher_token"
                value={formData.rancher_token}
                onChange={handleInputChange}
                placeholder="token-xxxxx:xxxxxxxxxxxxxxxxxxxx"
                autoComplete="new-password"
              />
              <small>Stored server-side and never returned by the API.</small>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  name="rancher_verify_tls"
                  checked={!!formData.rancher_verify_tls}
                  onChange={handleInputChange}
                />
                Verify the management server's TLS certificate
              </label>
              <small>
                Leave unchecked if it uses a self-signed or private-CA certificate.
                Enable it when it has a publicly trusted cert.
              </small>
            </div>
          </div>
        )}

        {/* Kubeconfig section — hidden for agent and rancher modes */}
        {needsKubeconfig && (
          <div className="card">
            <h2>Kubeconfig</h2>
            <p style={{ color: 'var(--muted)', fontSize: '14px', marginBottom: '8px' }}>
              Paste your kubeconfig content or upload the file.
            </p>

            <div className="form-group">
              <label htmlFor="kubeconfig_file">Upload kubeconfig file</label>
              <input
                type="file"
                id="kubeconfig_file"
                accept=".yaml,.yml,.conf,*"
                onChange={handleKubeconfigFile}
              />
              <small>Typically located at ~/.kube/config</small>
            </div>

            <div className="form-group">
              <label htmlFor="kubeconfig">Or paste kubeconfig content *</label>
              <textarea
                id="kubeconfig"
                name="kubeconfig"
                className="textarea"
                value={formData.kubeconfig}
                onChange={handleInputChange}
                placeholder="apiVersion: v1
clusters:
- cluster:
    server: https://...
  name: my-cluster
..."
                rows={12}
                style={{
                  width: '100%',
                  fontFamily: 'monospace',
                  resize: 'vertical',
                }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
              <button
                type="button"
                className="button button-secondary"
                onClick={handleTestConnection}
                disabled={testing || !formData.kubeconfig.trim()}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </button>

              {testResult && (
                <div className={`connection-result ${testResult.success ? 'connection-success' : 'connection-error'}`}>
                  {testResult.success ? (
                    <>
                      <span className="connection-check">&#10003;</span>
                      <span>
                        Connected — K8s <strong>{testResult.version}</strong> &nbsp;|&nbsp;
                        <strong>{testResult.node_count}</strong> node{testResult.node_count !== 1 ? 's' : ''}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="connection-cross">&#10007;</span>
                      <span>{testResult.message}</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className="button button-primary"
            disabled={loading || (needsKubeconfig && !testResult?.success)}
          >
            {loading
              ? ((isAgentMode || (isRancherMode && rancherSelfService)) ? 'Registering...' : 'Importing...')
              : ((isAgentMode || (isRancherMode && rancherSelfService)) ? 'Register Cluster' : 'Import Cluster')
            }
          </button>
          <button type="button" className="button button-secondary" onClick={() => navigate('/clusters/add')} disabled={loading}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default ImportCluster;
